import { useEffect, useState, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { formatCurrency, formatDate, getStatusColor } from '../lib/utils';
import { 
  Plus, Search, Edit2, ArrowRightCircle, Trash2, Eye,
  Printer, Mail, Building2, Phone, Calendar, FileText,
  CheckCircle, XCircle, Link, Copy, Check, Calculator, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import PricingCalculatorModal from '../components/PricingCalculatorModal';
import AIEmailComposer from '../components/AIEmailComposer';

const statusOptions = ['draft', 'sent', 'approved', 'declined'];
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Quotes() {
  const { 
    quotes, customers, fetchQuotes, fetchCustomers, 
    createQuote, updateQuote, convertQuoteToJob, createCustomer 
  } = useApp();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    notes: '',
    status: 'draft',
    line_items: [{ description: '', quantity: 1, unit_price: '' }]
  });

  // Quote preview modal state
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [portalLink, setPortalLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // AI Email composer state
  const [showAIEmail, setShowAIEmail] = useState(false);
  const [aiEmailType, setAiEmailType] = useState('quote_send');
  
  // Pricing calculator modal state
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [isCreateCustomerOpen, setIsCreateCustomerOpen] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({ name: '', company: '', email: '', phone: '' });

  const formatPhoneInput = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length < 4) return `(${digits}`;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleCreateInlineCustomer = async () => {
    if (!newCustomerForm.name.trim() && !newCustomerForm.company.trim()) {
      toast.error('Name or Company is required');
      return;
    }
    setCreatingCustomer(true);
    try {
      const customer = await createCustomer({ ...newCustomerForm, status: 'lead', notes: '' });
      await fetchCustomers();
      setFormData((prev) => ({ ...prev, customer_id: customer.id }));
      setNewCustomerForm({ name: '', company: '', email: '', phone: '' });
      setIsCreateCustomerOpen(false);
      toast.success('Customer created and selected');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create customer');
    }
    setCreatingCustomer(false);
  };

  // Handle calculated item from pricing calculator
  const handleCalculatedItem = (calculatedData) => {
    // Add the calculated item to line items
    const newItem = {
      description: calculatedData.description || `${calculatedData.category} - Qty ${calculatedData.quantity}`,
      quantity: calculatedData.quantity || 1,
      unit_price: calculatedData.unit_price || calculatedData.selling_price || calculatedData.suggested_price || 0,
      pricing_category: calculatedData.pricing_category || calculatedData.category,
      pricing_data: calculatedData.pricing_data,
      cost_snapshot: calculatedData.cost_snapshot
    };
    
    // If the last item is empty, replace it; otherwise add a new one
    const lastItem = formData.line_items[formData.line_items.length - 1];
    if (lastItem && !lastItem.description && lastItem.quantity === 1 && lastItem.unit_price === 0) {
      const newItems = [...formData.line_items];
      newItems[newItems.length - 1] = newItem;
      setFormData({ ...formData, line_items: newItems });
    } else {
      setFormData({ ...formData, line_items: [...formData.line_items, newItem] });
    }
    
    setIsCalculatorOpen(false);
    toast.success('Item added from calculator!');
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    await Promise.all([fetchQuotes(params), fetchCustomers()]);
    setLoading(false);
  }, [fetchCustomers, fetchQuotes, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    
    // Convert line items to proper numbers before submit
    const cleanedLineItems = formData.line_items.map(item => ({
      ...item,
      quantity: parseFloat(item.quantity) || 0,
      unit_price: parseFloat(item.unit_price) || 0
    }));
    
    try {
      if (editingQuote) {
        await updateQuote(editingQuote.id, {
          line_items: cleanedLineItems,
          notes: formData.notes,
          status: formData.status
        });
        toast.success('Quote updated');
      } else {
        await createQuote({ ...formData, line_items: cleanedLineItems });
        toast.success('Quote created');
      }
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save quote');
    }
  };

  const handleConvert = async (quoteId) => {
    if (window.confirm('Convert this quote to a job?')) {
      try {
        await convertQuoteToJob(quoteId);
        toast.success('Quote converted to job');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to convert quote');
      }
    }
  };

  const handleEdit = (quote) => {
    if (quote.job_id) {
      toast.error('Cannot edit quote that has been converted to job');
      return;
    }
    setEditingQuote(quote);
    setFormData({
      customer_id: quote.customer_id,
      notes: quote.notes || '',
      status: quote.status,
      line_items: quote.line_items.length > 0 ? quote.line_items : [{ description: '', quantity: 1, unit_price: 0 }]
    });
    setIsDialogOpen(true);
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: '', unit_price: '' }]
    });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.line_items];
    // Store as string to avoid prepending zeros, convert on submit
    newItems[index][field] = value;
    setFormData({ ...formData, line_items: newItems });
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      const newItems = formData.line_items.filter((_, i) => i !== index);
      setFormData({ ...formData, line_items: newItems });
    }
  };

  const calculateTotal = () => {
    return formData.line_items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + (qty * price);
    }, 0);
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      notes: '',
      status: 'draft',
      line_items: [{ description: '', quantity: '', unit_price: '' }]
    });
    setEditingQuote(null);
    setIsDialogOpen(false);
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown';
  };

  const getCustomer = (customerId) => {
    return customers.find(c => c.id === customerId);
  };

  const handleViewQuote = (quote) => {
    setSelectedQuote(quote);
    setPortalLink(null);
    setLinkCopied(false);
    setIsPreviewOpen(true);
  };

  const handleGenerateShareLink = async () => {
    if (!selectedQuote) return;
    
    setGeneratingLink(true);
    try {
      const customer = getCustomer(selectedQuote.customer_id);
      const response = await fetch(`${API_URL}/api/magic-links`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resource_type: 'quote',
          resource_id: selectedQuote.id,
          customer_email: customer?.email || null,
          expires_in_days: 7
        })
      });

      if (response.ok) {
        const data = await response.json();
        const link = `${window.location.origin}/portal/${data.token}`;
        setPortalLink(link);
        toast.success('Share link created (expires in 7 days)');
      } else {
        toast.error('Failed to generate share link');
      }
    } catch (err) {
      toast.error('Failed to generate share link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyLink = async () => {
    if (portalLink) {
      await navigator.clipboard.writeText(portalLink);
      setLinkCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEmail = async () => {
    const customer = getCustomer(selectedQuote?.customer_id);
    if (!customer?.email) {
      toast.error('Customer has no email address');
      return;
    }
    // For now, show a toast with instructions
    // In production, this would integrate with an email service
    toast.success(`Quote ready to email to ${customer.email}. Email integration coming soon!`);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="quotes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold font-heading uppercase tracking-tight text-white">Quotes</h1>
          <p className="text-slate-300 mt-1">{quotes.length} total quotes</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="neon-glow" data-testid="add-quote-btn" onClick={() => resetForm()}>
              <Plus className="h-4 w-4 mr-2" /> New Quote
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">
                {editingQuote ? 'Edit Quote' : 'New Quote'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Customer *</Label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.customer_id}
                      onValueChange={(val) => setFormData({ ...formData, customer_id: val })}
                      disabled={!!editingQuote}
                    >
                      <SelectTrigger className="flex-1" data-testid="quote-customer-select">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name || c.company}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!editingQuote && (
                      <Button type="button" variant="outline" onClick={() => setIsCreateCustomerOpen(true)} data-testid="quote-create-customer-inline-btn">
                        New Customer
                      </Button>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(val) => setFormData({ ...formData, status: val })}
                  >
                    <SelectTrigger data-testid="quote-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Calculator Shortcut */}
              <div className="p-3 bg-teal-500/10 border border-teal-500/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <p className="text-teal-400 font-medium">Need to calculate pricing?</p>
                    <p className="text-gray-500 text-xs mt-0.5">Use the pricing calculator for accurate quotes</p>
                  </div>
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    className="border-teal-500/50 text-teal-500 hover:bg-teal-500/10"
                    onClick={() => setIsCalculatorOpen(true)}
                    data-testid="quote-open-calculator-btn"
                  >
                    <Calculator className="h-4 w-4 mr-1" /> Calculate
                  </Button>
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Line Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                    <Plus className="h-3 w-3 mr-1" /> Add Item
                  </Button>
                </div>
                {formData.line_items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-6">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                        data-testid={`line-item-desc-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                        data-testid={`line-item-qty-${idx}`}
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(idx, 'unit_price', e.target.value)}
                        data-testid={`line-item-price-${idx}`}
                      />
                    </div>
                    <div className="col-span-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLineItem(idx)}
                        disabled={formData.line_items.length === 1}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="text-right font-bold text-lg">
                  Total: {formatCurrency(calculateTotal())}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  data-testid="quote-notes-input"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="quote-submit-btn">
                  {editingQuote ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="quote-filter-status">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {statusOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Quotes List */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : quotes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No quotes found</p>
              <Button variant="link" onClick={() => setIsDialogOpen(true)}>
                Create your first quote
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Quote #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotes.map((quote, idx) => (
                  <TableRow 
                    key={quote.id} 
                    className={`cursor-pointer transition-colors ${idx % 2 === 0 ? 'bg-transparent' : 'bg-gray-50'} hover:bg-gray-50/50`}
                    data-testid={`quote-row-${quote.id}`}
                    onClick={() => handleViewQuote(quote)}
                  >
                    <TableCell className="font-mono text-sm">
                      #{quote.id.slice(0, 8)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {getCustomerName(quote.customer_id)}
                    </TableCell>
                    <TableCell>{quote.line_items.length} items</TableCell>
                    <TableCell className="font-bold">{formatCurrency(quote.total)}</TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(quote.status)}>
                        {quote.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {formatDate(quote.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewQuote(quote)}
                          data-testid={`view-quote-${quote.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {!quote.job_id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleConvert(quote.id)}
                            data-testid={`convert-quote-${quote.id}`}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="Convert to Order"
                          >
                            <ArrowRightCircle className="h-4 w-4" />
                          </Button>
                        )}
                        {quote.job_id && (
                          <Badge variant="outline" className="text-green-600 border-green-500/50 bg-green-50">
                            Converted
                          </Badge>
                        )}
                        {!quote.job_id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(quote)}
                            data-testid={`edit-quote-${quote.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Quote Preview Modal */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto print:max-w-full print:max-h-full print:overflow-visible" data-testid="quote-preview-modal">
          <DialogHeader className="print:hidden">
            <div className="flex items-center justify-between">
              <DialogTitle className="font-heading uppercase flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Quote Preview
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleGenerateShareLink}
                  disabled={generatingLink}
                  data-testid="share-quote-btn"
                >
                  <Link className="h-4 w-4 mr-2" /> 
                  {generatingLink ? 'Creating...' : 'Share Link'}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    setAiEmailType(selectedQuote?.status === 'sent' ? 'quote_followup' : 'quote_send');
                    setShowAIEmail(true);
                  }}
                  className="bg-purple-50 border-purple-200 text-purple-600 hover:bg-purple-100"
                  data-testid="ai-email-quote-btn"
                >
                  <Sparkles className="h-4 w-4 mr-2" /> AI Draft
                </Button>
                <Button variant="outline" size="sm" onClick={handleEmail} data-testid="email-quote-btn">
                  <Mail className="h-4 w-4 mr-2" /> Email
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-quote-btn">
                  <Printer className="h-4 w-4 mr-2" /> Print
                </Button>
              </div>
            </div>
            {/* Share Link Display */}
            {portalLink && (
              <div className="mt-3 p-3 bg-teal-500/10 border border-teal-500/30 rounded-lg">
                <p className="text-sm text-[var(--text-secondary)] mb-2">Customer portal link (expires in 7 days):</p>
                <div className="flex items-center gap-2">
                  <Input 
                    value={portalLink} 
                    readOnly 
                    className="text-xs bg-[var(--input-bg)] border-[var(--input-border)]"
                  />
                  <Button 
                    size="sm" 
                    onClick={handleCopyLink}
                    className="bg-teal-500 hover:bg-teal-600"
                  >
                    {linkCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}
          </DialogHeader>

          {selectedQuote && (() => {
            const customer = getCustomer(selectedQuote.customer_id);
            const statusIcon = selectedQuote.status === 'approved' ? (
              <CheckCircle className="h-5 w-5 text-green-400" />
            ) : selectedQuote.status === 'declined' ? (
              <XCircle className="h-5 w-5 text-red-400" />
            ) : null;

            return (
              <div className="quote-preview space-y-6 p-6 bg-white rounded-lg border border-gray-200 shadow-sm print:border-none print:p-0 print:shadow-none">
                {/* Quote Header */}
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold font-heading uppercase tracking-tight text-blue-600">
                      QUOTE
                    </h2>
                    <p className="text-gray-500 font-mono text-sm mt-1">
                      #{selectedQuote.id.slice(0, 8).toUpperCase()}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 justify-end mb-2">
                      {statusIcon}
                      <Badge className={getStatusColor(selectedQuote.status)} data-testid="quote-status-badge">
                        {selectedQuote.status.toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500">
                      <Calendar className="h-3 w-3 inline mr-1" />
                      Created: {formatDate(selectedQuote.created_at)}
                    </p>
                    {selectedQuote.job_id && (
                      <p className="text-sm text-green-600 mt-1">
                        Converted to Order
                      </p>
                    )}
                  </div>
                </div>

                <Separator className="bg-gray-200" />

                {/* Customer Info */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                      Prepared For
                    </h3>
                    {customer ? (
                      <div className="space-y-1">
                        <p className="font-bold text-lg text-gray-900">{customer.name}</p>
                        {customer.company && (
                          <p className="text-gray-600 flex items-center gap-1">
                            <Building2 className="h-3 w-3" /> {customer.company}
                          </p>
                        )}
                        {customer.email && (
                          <p className="text-gray-600 flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {customer.email}
                          </p>
                        )}
                        {customer.phone && (
                          <p className="text-gray-600 flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {customer.phone}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500">Customer not found</p>
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                      From
                    </h3>
                    <div className="space-y-1">
                      <p className="font-bold text-lg text-gray-900">SignGuy AI</p>
                      <p className="text-gray-600">Your Professional Sign Shop</p>
                    </div>
                  </div>
                </div>

                <Separator className="bg-gray-200" />

                {/* Line Items Table */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wide">
                    Quote Details
                  </h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="text-left p-3 text-sm font-semibold text-gray-700">Description</th>
                          <th className="text-center p-3 text-sm font-semibold text-gray-700 w-20">Qty</th>
                          <th className="text-right p-3 text-sm font-semibold text-gray-700 w-28">Unit Price</th>
                          <th className="text-right p-3 text-sm font-semibold text-gray-700 w-28">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedQuote.line_items && selectedQuote.line_items.length > 0 ? (
                          selectedQuote.line_items.map((item, idx) => (
                            <tr key={idx} className={idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}>
                              <td className="p-3 text-sm text-gray-900">{item.description}</td>
                              <td className="p-3 text-sm text-center text-gray-900">{item.quantity}</td>
                              <td className="p-3 text-sm text-right text-gray-900">{formatCurrency(item.unit_price)}</td>
                              <td className="p-3 text-sm text-right font-medium text-gray-900">
                                {formatCurrency(item.quantity * item.unit_price)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="p-3 text-sm text-center text-gray-500">
                              No line items
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <Separator className="bg-gray-200" />
                    <div className="flex justify-between font-bold text-lg">
                      <span className="text-gray-900">Total:</span>
                      <span className="text-blue-600">
                        {formatCurrency(selectedQuote.total)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {selectedQuote.notes && (
                  <>
                    <Separator className="bg-gray-200" />
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                        Notes
                      </h3>
                      <p className="text-sm p-3 bg-gray-100 rounded-lg whitespace-pre-wrap text-gray-800">
                        {selectedQuote.notes}
                      </p>
                    </div>
                  </>
                )}

                {/* Terms / Footer */}
                <Separator className="bg-gray-200" />
                <div className="text-sm text-gray-600 space-y-2">
                  <p className="font-medium text-gray-700">Terms & Conditions:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>This quote is valid for 30 days from the date issued</li>
                    <li>50% deposit required upon approval to begin production</li>
                    <li>Balance due upon completion</li>
                    <li>Prices subject to change if specifications are modified</li>
                  </ul>
                </div>

                {/* Footer */}
                <div className="text-center text-xs text-gray-500 print:mt-8">
                  <p>Thank you for your business!</p>
                  <p className="mt-1">SignGuy AI - Your Professional Sign Shop</p>
                </div>

                {/* Actions (hidden in print) */}
                <div className="flex justify-between print:hidden">
                  <div className="flex gap-2">
                    {!selectedQuote.job_id && (
                      <Button 
                        variant="outline" 
                        onClick={() => { setIsPreviewOpen(false); handleEdit(selectedQuote); }}
                      >
                        <Edit2 className="h-4 w-4 mr-2" /> Edit Quote
                      </Button>
                    )}
                    {!selectedQuote.job_id && (
                      <Button 
                        onClick={async () => {
                          await handleConvert(selectedQuote.id);
                          setIsPreviewOpen(false);
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <ArrowRightCircle className="h-4 w-4 mr-2" /> Convert to Order
                      </Button>
                    )}
                    {!selectedQuote.job_id && (
                      <Button 
                        variant="outline"
                        onClick={() => {
                          const customer = getCustomer(selectedQuote.customer_id);
                          if (customer?.email) {
                            const subject = encodeURIComponent(`Quote #${selectedQuote.id.slice(0, 8).toUpperCase()} from SignGuy AI`);
                            const body = encodeURIComponent(`Dear ${customer.name},\n\nPlease find attached your quote #${selectedQuote.id.slice(0, 8).toUpperCase()} for ${formatCurrency(selectedQuote.total)}.\n\nThis quote is valid for 30 days.\n\nBest regards,\nSignGuy AI`);
                            window.open(`mailto:${customer.email}?subject=${subject}&body=${body}`, '_blank');
                          } else {
                            toast.error('Customer email not found');
                          }
                        }}
                      >
                        <Mail className="h-4 w-4 mr-2" /> Email
                      </Button>
                    )}
                  </div>
                  <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>
                    Close
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={isCreateCustomerOpen} onOpenChange={setIsCreateCustomerOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Create Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={newCustomerForm.name} onChange={(e) => setNewCustomerForm({ ...newCustomerForm, name: e.target.value })} data-testid="quote-inline-customer-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input value={newCustomerForm.company} onChange={(e) => setNewCustomerForm({ ...newCustomerForm, company: e.target.value })} data-testid="quote-inline-customer-company-input" />
              </div>
            </div>
            <p className="text-xs text-gray-500">Name or Company is required.</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={newCustomerForm.email} onChange={(e) => setNewCustomerForm({ ...newCustomerForm, email: e.target.value })} data-testid="quote-inline-customer-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={newCustomerForm.phone} onChange={(e) => setNewCustomerForm({ ...newCustomerForm, phone: formatPhoneInput(e.target.value) })} data-testid="quote-inline-customer-phone-input" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsCreateCustomerOpen(false)}>Cancel</Button>
              <Button type="button" onClick={handleCreateInlineCustomer} disabled={creatingCustomer} data-testid="quote-inline-customer-submit-btn">
                {creatingCustomer ? 'Creating...' : 'Create Customer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pricing Calculator Modal */}
      <PricingCalculatorModal
        isOpen={isCalculatorOpen}
        onClose={() => setIsCalculatorOpen(false)}
        onItemCalculated={handleCalculatedItem}
      />

      {/* AI Email Composer Modal */}
      <AIEmailComposer
        isOpen={showAIEmail}
        onClose={() => setShowAIEmail(false)}
        emailType={aiEmailType}
        context={{
          customer_name: selectedQuote ? getCustomer(selectedQuote.customer_id)?.name : '',
          customer_email: selectedQuote ? getCustomer(selectedQuote.customer_id)?.email : '',
          quote_number: selectedQuote?.id?.slice(0, 8).toUpperCase(),
          amount: selectedQuote?.total,
          company_name: 'SignGuy AI'
        }}
      />
    </div>
  );
}
